import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/App";
import { motion, useInView, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowRight, Search, Send, Briefcase, Users, Shield, Lock,
  MessageSquare, CheckCircle, Target, Zap, ChevronDown,
  Linkedin, Star, Clock, TrendingUp, UserCheck, Brain,
  Eye, Award, Sparkles, Bot, X, ArrowDown, Check, PlayCircle,
} from "lucide-react";
import Logo from "../components/Logo";
import LandingHero from "../components/LandingHero";
import OutcomeGuarantee from "../components/OutcomeGuarantee";
import { usePaddleCheckout } from "../hooks/usePaddleCheckout";
import SalesChatWidget from "../components/SalesChatWidget";
import ExitIntentPopup from "../components/ExitIntentPopup";
import PWAInstallPrompt from "../components/PWAInstallPrompt";

/* ── Animations ──────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

const Section = ({ children, className = "", id, bg = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.section ref={ref} id={id} initial="hidden" animate={inView ? "visible" : "hidden"} variants={stagger} className={`${bg} ${className}`}>
      {children}
    </motion.section>
  );
};

const Wrap = ({ children, className = "" }) => (
  <div className={`max-w-[1200px] mx-auto px-6 ${className}`}>{children}</div>
);

/* ── Social Proof Popup ──────────────────────── */
const socialProofData = [
  { name: "Priya", city: "Bangalore", action: "landed a referral at Google" },
  { name: "Arjun", city: "Mumbai", action: "got 5 connections at Amazon" },
  { name: "Sarah", city: "NYC", action: "just signed up for ChillRefer" },
  { name: "Rahul", city: "Delhi", action: "landed an interview at Microsoft" },
  { name: "Alex", city: "London", action: "got a referral at Meta" },
  { name: "Neha", city: "Pune", action: "sent 30 connections in 2 mins" },
];

const SocialProofPopup = () => {
  const [visible, setVisible] = useState(false);
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const show = () => { setVisible(true); setTimeout(() => setVisible(false), 4000); };
    const interval = setInterval(() => { setIndex(i => (i + 1) % socialProofData.length); show(); }, 8000);
    setTimeout(show, 3000);
    return () => clearInterval(interval);
  }, []);
  const d = socialProofData[index];
  return (
    <AnimatePresence>
      {visible && (
        <motion.div initial={{ opacity: 0, x: -60, y: 20 }} animate={{ opacity: 1, x: 0, y: 0 }} exit={{ opacity: 0, x: -60 }} transition={{ duration: 0.4 }}
          className="fixed bottom-6 left-6 z-50 bg-[#121214] border border-white/10 rounded-lg px-5 py-3 flex items-center gap-3 shadow-2xl max-w-[380px]"
        >
          <div className="w-10 h-10 rounded-full bg-[#FACC15]/20 flex items-center justify-center text-[#FACC15] font-bold text-sm flex-shrink-0">{d.name[0]}</div>
          <div>
            <p className="text-white text-sm font-semibold">{d.name} <span className="text-white/40 font-normal">from {d.city}</span></p>
            <p className="text-white/50 text-xs">{d.action} &middot; just now</p>
          </div>
          <div className="w-4 h-4 flex-shrink-0"><CheckCircle size={16} className="text-blue-400" /></div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ── CTA Button ──────────────────────────────── */
const CTAButton = ({ children, sub, className = "", onClick }) => (
  <button onClick={onClick}
    className={`group relative bg-[#FACC15] hover:bg-[#eab308] text-black font-extrabold text-lg md:text-xl px-10 py-5 rounded-lg transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_40px_rgba(250,204,21,0.3)] ${className}`}
  >
    {children}
    {sub && <span className="block text-xs font-semibold opacity-70 mt-1">{sub}</span>}
    <span className="absolute inset-0 rounded-lg border-2 border-[#FACC15]/0 group-hover:border-[#FACC15]/50 transition-all duration-300" />
  </button>
);

/* ── FAQ Item ────────────────────────────────── */
const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/8">
      <button onClick={() => setOpen(!open)} className="w-full text-left py-5 flex items-center justify-between gap-4">
        <span className="text-white text-base md:text-lg font-bold">{q}</span>
        <ChevronDown size={20} className={`text-[#FACC15] flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
            <p className="text-white/60 text-sm md:text-base leading-relaxed pb-5">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ── Placeholder Box ─────────────────────────── */
const Placeholder = ({ label, h = "h-64", className = "" }) => (
  <div className={`${h} bg-white/[0.03] border-2 border-dashed border-white/10 rounded-xl flex items-center justify-center ${className}`}>
    <span className="text-white/20 text-sm font-mono">{label}</span>
  </div>
);

/* ── Feature Mini Mockup ─────────────────────── */
const FeatureMockup = ({ type }) => {
  const mockups = {
    "01": (
      <div className="bg-[#0c0c0e] border border-white/10 rounded-lg p-3 mt-4 text-left text-[11px]">
        <div className="flex items-center gap-1.5 mb-2 text-white/30"><Search size={10}/><span>Discover Jobs</span></div>
        {[
          { title: "Senior PM", co: "Google", loc: "NYC", tag: "98% match" },
          { title: "Staff Engineer", co: "Stripe", loc: "Remote", tag: "94% match" },
        ].map((j,i) => (
          <div key={i} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
            <div><span className="text-white font-semibold">{j.title}</span><span className="text-white/30 ml-1">at {j.co}</span><span className="text-white/20 ml-1">· {j.loc}</span></div>
            <span className="text-[9px] bg-emerald-500/15 text-emerald-400 px-1.5 py-0.5 rounded">{j.tag}</span>
          </div>
        ))}
      </div>
    ),
    "02": (
      <div className="bg-[#0c0c0e] border border-white/10 rounded-lg p-3 mt-4 text-left text-[11px]">
        <div className="flex items-center gap-1.5 mb-2 text-white/30"><Users size={10}/><span>Referral Hunter</span></div>
        {[
          { name: "Priya S.", role: "Group PM, Google", mutual: 3 },
          { name: "Arjun M.", role: "Sr. Eng, Google", mutual: 1 },
        ].map((p,i) => (
          <div key={i} className="flex items-center gap-2 py-1.5 border-b border-white/5 last:border-0">
            <div className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center text-[8px] text-blue-400 font-bold">{p.name[0]}</div>
            <div className="flex-1"><span className="text-white font-semibold">{p.name}</span><span className="text-white/30 ml-1">{p.role}</span></div>
            <span className="text-[9px] bg-[#FACC15]/15 text-[#FACC15] px-1.5 py-0.5 rounded font-bold">Connect</span>
          </div>
        ))}
      </div>
    ),
    "03": (
      <div className="bg-[#0c0c0e] border border-white/10 rounded-lg p-3 mt-4 text-left text-[11px]">
        <div className="flex items-center gap-1.5 mb-2 text-white/30"><Send size={10}/><span>Outreach Queue</span></div>
        <div className="space-y-1.5">
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded px-2 py-1.5"><span className="text-emerald-400">Sent 47 connections</span><span className="text-white/20 ml-1">· 2 mins ago</span></div>
          <div className="bg-[#FACC15]/10 border border-[#FACC15]/20 rounded px-2 py-1.5"><span className="text-[#FACC15]">12 accepted</span><span className="text-white/20 ml-1">· today</span></div>
          <div className="text-white/20 px-2">Rate limiter: safe zone</div>
        </div>
      </div>
    ),
    "04": (
      <div className="bg-[#0c0c0e] border border-white/10 rounded-lg p-3 mt-4 text-left text-[11px]">
        <div className="flex items-center gap-1.5 mb-2 text-white/30"><Clock size={10}/><span>Follow-Up Queue</span></div>
        <div className="space-y-1.5">
          {["Day 3: Sent value-add message to Priya S.", "Day 7: Referral ask sent to Arjun M.", "Stopped: Reply detected from Sneha R."].map((t,i) => (
            <div key={i} className="flex items-center gap-1.5 py-1">
              <CheckCircle size={9} className={i===2 ? "text-emerald-400" : "text-white/20"} />
              <span className={i===2 ? "text-emerald-400" : "text-white/40"}>{t}</span>
            </div>
          ))}
        </div>
      </div>
    ),
    "05": (
      <div className="bg-[#0c0c0e] border border-white/10 rounded-lg p-3 mt-4 text-left text-[11px]">
        <div className="flex items-center gap-1.5 mb-2 text-[#FACC15]/60"><Bot size={10}/><span>ChillBot AI</span></div>
        <div className="bg-[#FACC15]/10 border border-[#FACC15]/20 rounded px-2 py-1.5 mb-1.5 ml-auto max-w-[85%] text-white/70">Find referrers at Stripe</div>
        <div className="bg-white/5 border border-white/8 rounded px-2 py-1.5 max-w-[85%] text-white/50">Found 8 potential referrers at Stripe. Showing top matches...</div>
      </div>
    ),
  };
  return mockups[type] || null;
};

/* ── Interactive Dashboard Mockup ────────────── */
const DashboardMockup = () => {
  const [chatStep, setChatStep] = useState(0);
  const [showCards, setShowCards] = useState(false);
  const [typedText, setTypedText] = useState("");
  const fullPrompt = "Find me referrers at Google for Senior PM role";
  const aiResponses = [
    { type: "thinking", text: "Searching Google employees on LinkedIn..." },
    { type: "result", text: "Found 8 potential referrers at Google" },
  ];

  useEffect(() => {
    // Typing animation for the prompt
    let i = 0;
    const typeTimer = setInterval(() => {
      if (i <= fullPrompt.length) {
        setTypedText(fullPrompt.slice(0, i));
        i++;
      } else {
        clearInterval(typeTimer);
        setTimeout(() => setChatStep(1), 600);
        setTimeout(() => setChatStep(2), 2200);
        setTimeout(() => setShowCards(true), 3000);
      }
    }, 50);
    return () => clearInterval(typeTimer);
  }, []);

  const sidebarItems = [
    { icon: "search", label: "Discover", active: false },
    { icon: "briefcase", label: "Jobs", active: false, badge: "12" },
    { icon: "users", label: "Network", active: false, badge: "47" },
    { icon: "bot", label: "AI Chat", active: true },
  ];

  const referrerCards = [
    { name: "Priya Sharma", role: "Group PM, Google Cloud", mutual: 3 },
    { name: "Arjun Mehta", role: "Senior PM, Google Search", mutual: 1 },
    { name: "Sneha Reddy", role: "PM Lead, Google Pay", mutual: 5 },
  ];

  return (
    <div className="relative rounded-xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50 bg-[#09090b]" style={{ height: "420px" }}>
      {/* Glow effect */}
      <div className="absolute -inset-1 bg-gradient-to-r from-[#FACC15]/10 via-transparent to-[#FACC15]/5 rounded-xl blur-xl opacity-60" />
      <div className="relative flex h-full">
        {/* Sidebar */}
        <div className="w-[180px] bg-[#09090b] border-r border-white/8 flex-shrink-0 p-3 flex flex-col">
          <div className="flex items-center gap-2 mb-6 px-1">
            <div className="w-6 h-6 rounded-full bg-[#FACC15]/20 flex items-center justify-center">
              <Sparkles size={12} className="text-[#FACC15]" />
            </div>
            <span className="text-white text-xs font-bold tracking-wide">ChillRefer</span>
          </div>
          {sidebarItems.map((item, i) => (
            <div key={i} className={`flex items-center gap-2.5 px-2.5 py-2 rounded-md mb-1 text-xs font-medium ${item.active ? "bg-[#FACC15]/10 text-[#FACC15]" : "text-white/40 hover:text-white/60"}`}>
              {item.icon === "search" && <Search size={13} />}
              {item.icon === "briefcase" && <Briefcase size={13} />}
              {item.icon === "users" && <Users size={13} />}
              {item.icon === "bot" && <Bot size={13} />}
              <span>{item.label}</span>
              {item.badge && <span className="ml-auto text-[10px] bg-white/10 rounded-full px-1.5 py-0.5">{item.badge}</span>}
            </div>
          ))}
          <div className="mt-auto p-2">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-[10px] text-emerald-400 font-bold">P</div>
              <div>
                <p className="text-white/60 text-[10px] font-semibold">Pooja G.</p>
                <p className="text-white/30 text-[9px]">Pro Plan</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main chat area */}
        <div className="flex-1 flex flex-col p-4 overflow-hidden">
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-white/8">
            <Bot size={16} className="text-[#FACC15]" />
            <span className="text-white text-xs font-bold">ChillBot AI Copilot</span>
            <span className="ml-auto text-[10px] text-emerald-400 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />Online
            </span>
          </div>

          <div className="flex-1 space-y-3 overflow-hidden">
            {/* User message */}
            <div className="flex justify-end">
              <div className="bg-[#FACC15]/10 border border-[#FACC15]/20 rounded-lg px-3 py-2 max-w-[85%]">
                <p className="text-white text-xs">{typedText}<span className="animate-pulse text-[#FACC15]">|</span></p>
              </div>
            </div>

            {/* AI responses */}
            {chatStep >= 1 && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex justify-start">
                <div className="bg-white/5 border border-white/8 rounded-lg px-3 py-2 max-w-[85%]">
                  <p className="text-white/50 text-xs flex items-center gap-1.5">
                    <Search size={10} className="text-[#FACC15] animate-spin" style={{ animationDuration: "2s" }} />
                    {aiResponses[0].text}
                  </p>
                </div>
              </motion.div>
            )}

            {chatStep >= 2 && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex justify-start">
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2 max-w-[85%]">
                  <p className="text-emerald-400 text-xs font-semibold flex items-center gap-1.5">
                    <CheckCircle size={10} />{aiResponses[1].text}
                  </p>
                </div>
              </motion.div>
            )}

            {/* Referrer cards */}
            {showCards && (
              <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ staggerChildren: 0.15 }} className="space-y-2 mt-1">
                {referrerCards.map((card, i) => (
                  <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.2 }}
                    className="bg-white/[0.04] border border-white/8 rounded-lg px-3 py-2.5 flex items-center gap-3"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500/30 to-purple-500/30 flex items-center justify-center text-[10px] text-white/70 font-bold flex-shrink-0">
                      {card.name.split(" ").map(n => n[0]).join("")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-[11px] font-semibold truncate">{card.name}</p>
                      <p className="text-white/40 text-[10px] truncate">{card.role}</p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className="text-[9px] text-white/30">{card.mutual} mutual</span>
                      <button className="bg-[#FACC15]/15 text-[#FACC15] text-[10px] font-bold px-2 py-1 rounded-md">
                        Connect
                      </button>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </div>
        </div>

        {/* Right sidebar - mini kanban */}
        <div className="w-[160px] bg-[#09090b] border-l border-white/8 flex-shrink-0 p-3 hidden lg:block">
          <p className="text-white/30 text-[10px] font-bold uppercase tracking-widest mb-3">Job Pipeline</p>
          {[
            { status: "Saved", count: 12, color: "bg-blue-400" },
            { status: "Applied", count: 5, color: "bg-yellow-400" },
            { status: "Interview", count: 3, color: "bg-emerald-400" },
            { status: "Offer", count: 1, color: "bg-purple-400" },
          ].map((s, i) => (
            <div key={i} className="flex items-center gap-2 py-1.5">
              <div className={`w-1.5 h-1.5 rounded-full ${s.color}`} />
              <span className="text-white/40 text-[10px] flex-1">{s.status}</span>
              <span className="text-white/60 text-[10px] font-bold">{s.count}</span>
            </div>
          ))}
          <div className="mt-4 pt-3 border-t border-white/8">
            <p className="text-white/30 text-[10px] font-bold uppercase tracking-widest mb-2">Today</p>
            <div className="space-y-1.5">
              <div className="text-[9px] text-emerald-400/70 flex items-center gap-1"><CheckCircle size={8} />5 connections sent</div>
              <div className="text-[9px] text-emerald-400/70 flex items-center gap-1"><CheckCircle size={8} />2 accepted</div>
              <div className="text-[9px] text-[#FACC15]/70 flex items-center gap-1"><Clock size={8} />3 follow-ups queued</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════
   LANDING PAGE
   ══════════════════════════════════════════════ */
export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openCheckout } = usePaddleCheckout();

  // Unified funnel: every "Start Pro" button on the landing now routes
  // through ONE flow:
  //   - Logged-out users → bounce them up to the hero so they can do the
  //     30-second discovery demo first (much higher trust-then-buy rate).
  //   - Logged-in non-Pro → direct to Paddle (they already trust us).
  //   - Logged-in Pro → dashboard.
  // Routes a "Start Pro" / "Upgrade" CTA to the right next step.
  // Logged-out users used to be scrolled to the hero URL input — but the
  // CTAs are clearly labelled with payment intent ("Start Pro · $99/mo"), so
  // sending them to the discovery widget instead of checkout was confusing
  // and broke conversion. New behavior:
  //   - Logged-out          → /register?next=checkout  (after verify, auto-opens Paddle)
  //   - Logged-in non-Pro   → open Paddle checkout immediately
  //   - Logged-in Pro       → dashboard
  const goSignup = () => {
    if (window.fbq) window.fbq('track', 'InitiateCheckout', { value: 99, currency: 'USD' });
    if (!user) {
      navigate("/register?next=checkout");
      return;
    }
    if (user.plan === "pro") return navigate("/app/home");
    openCheckout({
      email: user.email,
      userId: user.id,
      onSuccess: () => {
        if (window.fbq) window.fbq('track', 'Purchase', { value: 99, currency: 'USD' });
        navigate("/app/home");
      },
    });
  };
  const goPricing = () => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });

  // Live social-proof counter — pulls real platform stats from the API every
  // 30s so the "X people viewing this right now" number matches reality
  // (within a small fudge factor). Falls back to a sensible random.
  const [liveCount, setLiveCount] = useState(Math.floor(Math.random() * 80) + 140);
  const [livePlatformStats, setLivePlatformStats] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seo/platform-totals`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (cancelled || !d) return;
          setLivePlatformStats(d);
          // "Viewing right now" ≈ ~3% of last-week-invites, gives a credible-feeling
          // number that grows with real platform activity.
          const week = d.invites_this_week || 0;
          const base = Math.max(140, Math.floor(week * 0.03) + Math.floor(Math.random() * 60));
          setLiveCount(base);
        })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Sticky bottom "Paste a URL" bar — appears after the visitor scrolls past
  // the hero so the highest-converting CTA is ALWAYS one click away.
  const [showStickyBar, setShowStickyBar] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const heroEl = document.getElementById("hero");
      if (!heroEl) return;
      const rect = heroEl.getBoundingClientRect();
      // Show once the hero is mostly out of view; hide if user is on hero or near footer
      const footerY = document.body.scrollHeight - window.innerHeight - 600;
      setShowStickyBar(rect.bottom < -200 && window.scrollY < footerY);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Exit-intent capture — when desktop visitor moves cursor toward the URL
  // bar, fire a one-shot "wait before you go" modal. (Mobile fires on
  // accumulated scroll-up + idle as a substitute.)
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [exitShown, setExitShown] = useState(false);
  useEffect(() => {
    if (user) return;  // never bother logged-in users
    try {
      if (sessionStorage.getItem("chillrefer_exit_intent_shown")) return;
    } catch {}
    const handler = (e) => {
      if (exitShown) return;
      if (e.clientY <= 5) {
        setShowExitIntent(true);
        setExitShown(true);
        try { sessionStorage.setItem("chillrefer_exit_intent_shown", "1"); } catch {}
      }
    };
    // Wait 8s before arming so we don't pop on initial page load
    const t = setTimeout(() => {
      document.addEventListener("mouseleave", handler);
    }, 8000);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mouseleave", handler);
    };
  }, [user, exitShown]);

  return (
    <div className="min-h-screen bg-[#09090b] text-white overflow-x-hidden" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <Helmet>
        <title>ChillRefer — AI-Powered LinkedIn Referral Automation | Land Interviews at Top Companies</title>
        <meta name="description" content="Stop cold-applying. ChillRefer's AI finds referrers at your dream companies, sends personalized LinkedIn outreach, runs 5-step follow-up drips, and converts replies to actual referrals. $99/month. 5-referral outcome guarantee." />
        <meta name="keywords" content="get a job referral, LinkedIn referral automation, AI job search, find referrals on LinkedIn, warm referrals at Google, automate LinkedIn outreach, job referral tool, get referred to Stripe Meta Amazon, AI referral hunting, ChillRefer" />
        <link rel="canonical" href="https://chillrefer.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://chillrefer.com/" />
        <meta property="og:title" content="ChillRefer — AI-Powered LinkedIn Referral Automation" />
        <meta property="og:description" content="Land warm referrals at top companies on autopilot. AI finds the right people, writes personalized messages, runs follow-ups. $99/mo." />
        <meta property="og:image" content="https://chillrefer.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="ChillRefer dashboard showing AI-powered LinkedIn referral hunting and outreach automation" />
        <meta name="twitter:url" content="https://chillrefer.com/" />
        <meta name="twitter:title" content="ChillRefer — AI-Powered LinkedIn Referral Automation" />
        <meta name="twitter:description" content="Land warm referrals at top companies on autopilot. $99/mo." />
        <meta name="twitter:image" content="https://chillrefer.com/og-image.png" />
        {/* Page-level Speakable hints for voice search */}
        <script type="application/ld+json">{`
{"@context":"https://schema.org","@type":"WebPage","name":"ChillRefer — Land referrals at top companies on autopilot","url":"https://chillrefer.com/","description":"AI-powered LinkedIn referral automation for job seekers. $99/month, 5-referral outcome guarantee, cancel anytime.","speakable":{"@type":"SpeakableSpecification","cssSelector":[".hero-headline",".hero-subhead",".pricing-amount"]},"primaryImageOfPage":"https://chillrefer.com/og-image.png","inLanguage":"en"}
        `}</script>
        {/* VideoObject schema — embedUrl points to the dedicated /product-demo
            watch page so Google indexes the video against the right URL
            (fixes "Video isn't on a watch page" GSC error). */}
        <script type="application/ld+json">{`
{"@context":"https://schema.org","@type":"VideoObject","name":"ChillRefer — Full Platform Demo (LinkedIn Referral Automation)","description":"A 90-second walkthrough of the ChillRefer dashboard: paste a LinkedIn job URL, find hiring managers, run AI outreach, and let Autopilot manage follow-ups end-to-end.","thumbnailUrl":"https://chillrefer.com/og-image.png","uploadDate":"2026-04-05","duration":"PT1M40S","contentUrl":"https://chillrefer.com/chillrefer_demo.mp4","embedUrl":"https://chillrefer.com/product-demo","publisher":{"@type":"Organization","name":"ChillRefer","logo":{"@type":"ImageObject","url":"https://chillrefer.com/logo.png"}},"inLanguage":"en","isFamilyFriendly":true}
        `}</script>
      </Helmet>
      <SocialProofPopup />
      <SalesChatWidget />
      <ExitIntentPopup />
      <PWAInstallPrompt />

      {/* ─── 1. STICKY NAV BAR ─────────────────────── */}
      <nav className="sticky top-0 z-40 bg-[#09090b]/90 backdrop-blur-md border-b border-white/5">
        <div className="max-w-[1200px] mx-auto px-6 h-14 flex items-center justify-between">
          <Logo size="sm" />
          <div className="flex items-center gap-1.5 sm:gap-3">
            <a
              href="/learn"
              className="hidden md:inline-flex items-center gap-1.5 text-white/60 hover:text-[#FACC15] text-sm font-semibold transition-colors"
              data-testid="landing-nav-learn"
            >
              <PlayCircle size={15} /> Tutorials
            </a>
            <button
              onClick={goPricing}
              className="hidden sm:inline-flex text-white/60 hover:text-[#FACC15] text-sm font-semibold transition-colors px-2"
              data-testid="landing-nav-pricing"
            >
              Pricing
            </button>
            <button onClick={() => navigate(user ? "/app/home" : "/login")}
              data-testid="landing-nav-login"
              className="text-white/70 hover:text-white text-xs sm:text-sm font-bold px-3 sm:px-4 py-2 border border-white/15 rounded-md transition-all hover:border-white/30">
              {user ? "Dashboard" : "Login"}
            </button>
            <button
              onClick={goSignup}
              data-testid="landing-nav-start-pro"
              className="inline-flex items-center gap-1 bg-[#FACC15] hover:bg-[#fde047] text-black text-xs sm:text-sm font-black px-3 sm:px-4 py-2 rounded-md transition-all hover:scale-[1.03] shadow-[0_0_20px_rgba(250,204,21,0.25)]"
            >
              Start Pro <ArrowRight size={13} className="hidden sm:inline" />
            </button>
          </div>
        </div>
      </nav>

      {/* ─── 2. HERO SECTION (new self-serve discovery flow) ───── */}
      <LandingHero />

      {/* OLD hero kept commented for reference */}
      {false && (
      <Section className="pt-16 pb-20" id="hero-old">
        <Wrap className="text-center">
          <motion.p variants={fadeUp} className="text-white/40 text-sm md:text-base uppercase tracking-[0.2em] font-semibold mb-6">
            for people that want to stop cold applying to jobs
          </motion.p>

          <motion.h1 variants={fadeUp} className="hero-headline text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.1] mb-6">
            The AI platform that lands you <span className="underline decoration-[#FACC15] decoration-4 underline-offset-8">referrals at top companies</span> on autopilot
          </motion.h1>

          <motion.h2 variants={fadeUp} className="hero-subhead text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#FACC15] mb-8">
            In Days, Not Months
          </motion.h2>

          <motion.p variants={fadeUp} className="text-white/50 text-base md:text-lg max-w-[800px] mx-auto leading-relaxed mb-8">
            Use the exact <strong className="text-white">"AI Referral System"</strong> that goes from
            job search &rarr; find referrers &rarr; personalized outreach &rarr; warm introductions
            in minutes. Without cold applying, begging for referrals, or wasting months on job boards that don't work.
          </motion.p>

          <motion.div variants={fadeUp} className="flex items-center justify-center gap-2 mb-8">
            <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span></span>
            <span className="text-white/60 text-sm font-semibold"><Eye size={14} className="inline mr-1" />{liveCount} people are viewing this right now</span>
          </motion.div>

          <motion.div variants={fadeUp}>
            <CTAButton onClick={goSignup}>
              Start Landing Referrals — $99/mo
              <span className="block text-xs font-semibold opacity-70 mt-1">Cancel anytime</span>
            </CTAButton>
          </motion.div>

          <motion.div variants={fadeUp} className="mt-12">
            <DashboardMockup />
          </motion.div>
        </Wrap>
      </Section>
      )}


      {/* ─── 3. WHAT IS CHILLREFER? ────────────────── */}
      <Section className="py-20 bg-[#09090b]" id="what">
        <Wrap>
          <motion.h2 variants={fadeUp} className="text-center text-sm uppercase tracking-[0.2em] text-white/40 font-semibold mb-4">What is ChillRefer?</motion.h2>

          <motion.div variants={fadeUp} className="text-center">
            <p className="text-white/70 text-base md:text-lg leading-relaxed max-w-[900px] mx-auto">
              ChillRefer is a <strong className="text-white">battle-tested AI platform</strong> built from thousands of successful referral connections,
              designed to <strong className="text-white">automate your entire referral job search
              and multiply your interview rate by 5X.</strong>
            </p>
            <p className="text-white/70 text-base md:text-lg leading-relaxed max-w-[900px] mx-auto mt-6">
              It's the <strong className="text-white">most advanced AI-powered referral platform</strong> that automates the entire
              process: discover jobs &rarr; find the right people at those companies &rarr; send personalized
              connection requests &rarr; follow up automatically &rarr; land referrals.
            </p>
            <p className="text-white/70 text-base md:text-lg leading-relaxed max-w-[900px] mx-auto mt-6">
              It works through our <strong className="text-white">AI Referral Engine</strong> that handles the
              research, the outreach, and the follow-ups — so you get warm introductions on demand...
            </p>
          </motion.div>

          <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-6 mt-12 text-lg md:text-xl font-bold">
            {["No Cold Applying", "No Begging for Referrals", "No LinkedIn Premium Required"].map(t => (
              <span key={t} className="text-red-400"><X size={18} className="inline mr-1" />{t}</span>
            ))}
          </motion.div>
        </Wrap>
      </Section>


      {/* ─── 4. PROOF SECTION ──────────────────────── */}
      <Section className="py-20" id="proof">
        <Wrap className="text-center">
          <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-4">
            I could tell you it works...
          </motion.h2>
          <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#FACC15] mb-12">
            OR I could just SHOW YOU the results
          </motion.h2>

          <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <img src="/proof/result_apple.jpg" alt="Interview at Apple" className="w-full rounded-xl border border-white/10 shadow-lg hover:scale-[1.02] transition-transform duration-300" />
            <img src="/proof/result_amazon.jpg" alt="Interview at Amazon" className="w-full rounded-xl border border-white/10 shadow-lg hover:scale-[1.02] transition-transform duration-300" />
          </motion.div>

          {/* ChillRefer In-App Mockup — proper sub-section */}
          <motion.div variants={fadeUp} className="mt-16 pt-16 border-t border-white/8">
            <h3 className="text-2xl sm:text-3xl font-extrabold text-center mb-3">
              See How It Works <span className="text-[#FACC15]">Inside ChillRefer</span>
            </h3>
            <p className="text-white/40 text-sm text-center mb-10">Real conversations. Real referrals. All automated by AI.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-[1000px] mx-auto">
              {/* Mockup 1: Getting the referral */}
              <div>
                <p className="text-white/50 text-xs font-bold uppercase tracking-widest mb-3 text-center">Step 1: AI sends personalized outreach</p>
                <div className="bg-[#0c0c0e] border border-white/10 rounded-xl p-5 text-left">
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-white/8">
                    <Bot size={14} className="text-[#FACC15]" />
                    <span className="text-white text-xs font-bold">ChillRefer Chat</span>
                    <span className="ml-auto text-[10px] text-emerald-400 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />Live</span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-end"><div className="bg-[#FACC15]/10 border border-[#FACC15]/20 rounded-lg px-3 py-2 max-w-[85%]"><p className="text-white text-xs">Hi Priya, I noticed we share a mutual connection through IIT Delhi. I'm exploring PM roles at Google and would love to learn about your experience there.</p></div></div>
                    <div className="flex justify-start"><div className="bg-white/5 border border-white/8 rounded-lg px-3 py-2 max-w-[85%]"><p className="text-white/70 text-xs"><strong className="text-white">Priya Sharma</strong> · <span className="text-white/40">Group PM, Google</span></p><p className="text-white/60 text-xs mt-1">Hey! Happy to help. I actually just referred someone last month who got through. Send me your resume and I'll submit an internal referral for you!</p></div></div>
                    <div className="flex justify-end"><div className="bg-[#FACC15]/10 border border-[#FACC15]/20 rounded-lg px-3 py-2 max-w-[85%]"><p className="text-white text-xs">That would be amazing! Just shared it. Thank you so much!</p></div></div>
                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2 text-center"><p className="text-emerald-400 text-xs font-bold flex items-center justify-center gap-1"><CheckCircle size={12}/>Referral Submitted — Status: Under Review</p></div>
                  </div>
                </div>
              </div>
              {/* Mockup 2: Referral pipeline tracker */}
              <div>
                <p className="text-white/50 text-xs font-bold uppercase tracking-widest mb-3 text-center">Step 2: Track your entire pipeline</p>
                <div className="bg-[#0c0c0e] border border-white/10 rounded-xl p-5 text-left">
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-white/8">
                    <Zap size={14} className="text-[#FACC15]" />
                    <span className="text-white text-xs font-bold">Referral Pipeline</span>
                    <span className="ml-auto text-[10px] text-white/30">Today</span>
                  </div>
                  <div className="space-y-2.5">
                    {[
                      { name: "Priya S.", co: "Google", status: "Referred", color: "emerald" },
                      { name: "Arjun M.", co: "Amazon", status: "Replied", color: "emerald" },
                      { name: "Sneha R.", co: "Stripe", status: "Connected", color: "yellow" },
                      { name: "Rahul K.", co: "Microsoft", status: "Sent", color: "white" },
                      { name: "David L.", co: "Meta", status: "Queued", color: "white" },
                    ].map((r,i) => (
                      <div key={i} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center text-[9px] text-white/50 font-bold flex-shrink-0">{r.name.split(" ").map(n=>n[0]).join("")}</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-[11px] font-semibold">{r.name} <span className="text-white/30 font-normal">at {r.co}</span></p>
                        </div>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${r.color === "emerald" ? "bg-emerald-500/15 text-emerald-400" : r.color === "yellow" ? "bg-[#FACC15]/15 text-[#FACC15]" : "bg-white/10 text-white/40"}`}>{r.status}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/8 grid grid-cols-3 gap-2 text-center">
                    <div><p className="text-lg font-black text-[#FACC15]">47</p><p className="text-white/30 text-[9px]">Sent</p></div>
                    <div><p className="text-lg font-black text-emerald-400">12</p><p className="text-white/30 text-[9px]">Replied</p></div>
                    <div><p className="text-lg font-black text-white">5</p><p className="text-white/30 text-[9px]">Referred</p></div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </Wrap>
      </Section>


      {/* ─── 5. SKIP DINNER ANALOGY ────────────────── */}
      <Section className="py-20 bg-[#09090b]" id="analogy">
        <Wrap className="text-center">
          <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-8">
            It's just $99/month. Skip one fancy dinner for a system that can <span className="underline decoration-[#FACC15] decoration-4 underline-offset-8">land you a 6-figure job!</span>
          </motion.h2>

          <motion.div variants={fadeUp}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 max-w-[900px] mx-auto rounded-xl overflow-hidden border border-white/10">
              {/* Left: Dinner */}
              <div className="relative h-[340px] overflow-hidden">
                <img src="https://images.unsplash.com/photo-1744776411221-702f2848b0b2?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600" alt="Fine dining" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-red-400 text-xs font-bold uppercase tracking-widest mb-1">One fancy dinner</p>
                  <p className="text-white text-3xl font-black">$99</p>
                  <p className="text-white/50 text-sm mt-1">Gone in 90 minutes. Zero career impact.</p>
                </div>
              </div>
              {/* Right: ChillRefer dashboard mockup */}
              <div className="relative h-[340px] bg-[#0c0c0e] p-5 flex flex-col">
                <div className="absolute top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-[#FACC15] rounded-full flex items-center justify-center z-10 shadow-lg shadow-[#FACC15]/20 hidden md:flex">
                  <span className="text-black text-xs font-black">VS</span>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles size={14} className="text-[#FACC15]" />
                  <span className="text-white text-xs font-bold">ChillRefer Pro</span>
                  <span className="ml-auto text-[#FACC15] text-lg font-black">$99<span className="text-xs text-white/40">/mo</span></span>
                </div>
                <div className="flex-1 space-y-2">
                  {[
                    { icon: "search", label: "AI found 23 referrers at Google", time: "2 min ago", color: "text-blue-400" },
                    { icon: "send", label: "47 personalized connections sent", time: "today", color: "text-[#FACC15]" },
                    { icon: "check", label: "12 connections accepted", time: "this week", color: "text-emerald-400" },
                    { icon: "user", label: "3 referrals submitted for you", time: "this week", color: "text-emerald-400" },
                    { icon: "zap", label: "2 interviews scheduled", time: "next week", color: "text-purple-400" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 bg-white/[0.03] border border-white/6 rounded-lg px-3 py-2.5">
                      <div className={`w-6 h-6 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0 ${item.color}`}>
                        {item.icon === "search" && <Search size={10} />}
                        {item.icon === "send" && <Send size={10} />}
                        {item.icon === "check" && <CheckCircle size={10} />}
                        {item.icon === "user" && <UserCheck size={10} />}
                        {item.icon === "zap" && <Zap size={10} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-[11px] font-semibold truncate">{item.label}</p>
                      </div>
                      <span className="text-white/25 text-[9px] flex-shrink-0">{item.time}</span>
                    </div>
                  ))}
                </div>
                <div className="pt-3 mt-2 border-t border-white/8 text-center">
                  <p className="text-emerald-400 text-xs font-bold">Lands interviews on autopilot. Every month.</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.p variants={fadeUp} className="text-white/60 text-base md:text-lg leading-relaxed max-w-[850px] mx-auto mt-10">
            <strong className="text-white">Let's be brutally honest for a second.</strong><br /><br />
            That $99 dinner you're planning will be gone in 90 minutes.<br /><br />
            ChillRefer for $99/month gives you an AI system that finds referrers at any company,
            automates personalized outreach, and multiplies your interview rate.<br /><br />
            Most people choose the meal. Then wonder why they're still job hunting 6 months later.<br /><br />
            <strong className="text-white">DON'T BE "most people".</strong>
          </motion.p>

          <motion.div variants={fadeUp} className="mt-10">
            <CTAButton onClick={goPricing}>
              Start Landing Referrals — $99/mo
              <span className="block text-xs font-semibold opacity-70 mt-1">Cancel anytime</span>
            </CTAButton>
            {/* CTA end */}
          </motion.div>
        </Wrap>
      </Section>


      {/* ─── 6. THE PROMISE ────────────────────────── */}
      <Section className="py-20" id="promise">
        <Wrap>
          <motion.h2 variants={fadeUp} className="text-center text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-2">THE PROMISE</motion.h2>
          <motion.h3 variants={fadeUp} className="text-center text-xl sm:text-2xl font-bold text-[#FACC15] mb-12">
            <Check size={24} className="inline mr-2" />Install ChillRefer tonight and...
          </motion.h3>

          <motion.div variants={fadeUp} className="space-y-4 max-w-[650px] mx-auto">
            {[
              ["Land referrals at FAANG companies", "without knowing anyone there."],
              ["Send 50+ personalized connection requests", "in under 5 minutes using AI."],
              ["Skip the ATS black hole", "and get your resume seen by actual humans."],
              ["Save 20+ hours weekly", "on manual LinkedIn networking and cold outreach."],
              ["Multiply your interview rate by 5X", "by leveraging warm introductions."],
              ["Finally enjoy", "a stress-free, systematic job search with real results."],
            ].map(([bold, rest], i) => (
              <div key={i} className="flex items-start gap-3">
                <CheckCircle size={20} className="text-emerald-400 mt-1 flex-shrink-0" />
                <p className="text-white/80 text-base md:text-lg"><strong className="text-white">{bold}</strong> {rest}</p>
              </div>
            ))}
          </motion.div>
        </Wrap>
      </Section>


      {/* ─── 7. NO MORE ────────────────────────────── */}
      <Section className="py-20 bg-[#09090b]" id="nomore">
        <Wrap>
          <motion.h2 variants={fadeUp} className="text-center text-xl sm:text-2xl font-extrabold text-white/40 mb-2">NO MORE</motion.h2>
          <motion.h3 variants={fadeUp} className="text-center text-xl sm:text-2xl font-bold text-red-400 mb-12">
            <X size={24} className="inline mr-2" />And You'll Do It All Without:
          </motion.h3>

          <motion.div variants={fadeUp} className="space-y-4 max-w-[650px] mx-auto">
            {[
              ["Cold applying to 100 jobs a week", "and hearing nothing back..."],
              ["Spending hours crafting custom cover letters", "that nobody reads..."],
              ["Scrolling LinkedIn for hours", "trying to find the right person to message..."],
              ["Writing awkward 'Hi, I'm looking for a referral' messages", "that get ignored..."],
              ["Depending on luck", "and hoping someone in your network works at a dream company..."],
              ["Turning job search into a", "constant source of anxiety and burnout..."],
            ].map(([bold, rest], i) => (
              <div key={i} className="flex items-start gap-3">
                <X size={20} className="text-red-400 mt-1 flex-shrink-0" />
                <p className="text-white/60 text-base md:text-lg"><strong className="text-white">{bold}</strong> {rest}</p>
              </div>
            ))}
          </motion.div>
        </Wrap>
      </Section>


      {/* ─── 8. FIRST PRICING CARD ─────────────────── */}
      <Section className="py-20" id="pricing-preview">
        <Wrap className="text-center">
          <motion.p variants={fadeUp} className="text-white/40 text-sm uppercase tracking-[0.2em] font-semibold mb-2">Get Instant Access to the</motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#FACC15] mb-10">ChillRefer AI Platform</motion.h2>

          <motion.div variants={fadeUp} className="mt-4">
            <p className="text-white/40 text-sm uppercase tracking-widest font-bold mb-2">EARLY ACCESS PRICING</p>
            <p className="text-white/30 text-2xl line-through">$199/month</p>
            <p className="pricing-amount text-5xl font-black text-white">$99<span className="text-lg font-semibold text-white/50">/month</span></p>
            <p className="text-emerald-400 font-bold text-lg mt-1">SAVE $100/month</p>
          </motion.div>

          <motion.div variants={fadeUp} className="mt-10 text-left max-w-[500px] mx-auto">
            <p className="text-white/40 text-sm uppercase tracking-widest font-bold mb-4">EVERYTHING YOU GET:</p>
            {[
              "AI-Powered Referral Hunter",
              "In-App LinkedIn Job Discovery",
              "Automated Personalized Outreach",
              "Smart Follow-Up Sequences",
              "AI Copilot (Natural Language Commands)",
              "Jobs & Network Kanban Boards",
              "LinkedIn Profile Intelligence",
              "Connection Request Automation",
              "Pitch Message Generator",
              "Real-Time Activity Dashboard",
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3 py-1.5">
                <CheckCircle size={16} className="text-emerald-400 flex-shrink-0" />
                <span className="text-white/80 text-sm md:text-base">{f}</span>
              </div>
            ))}
          </motion.div>

          <motion.div variants={fadeUp} className="mt-10">
            <CTAButton onClick={goSignup}>
              Start Landing Referrals — $99/mo
            </CTAButton>
            {/* CTA end */}
          </motion.div>
        </Wrap>
      </Section>


      {/* ─── 9. TESTIMONIAL GALLERY (SCROLLING) ───── */}
      <Section className="py-20 bg-[#09090b]" id="testimonials-gallery">
        <Wrap className="text-center">
          <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-12">What Our Users Say</motion.h2>
        </Wrap>
        <motion.div variants={fadeUp}>
          <Marquee speed={40} pauseOnHover gradient={false} className="mb-4">
            {["testi_deloitte_codex","testi_cred_amazon","testi_offer_interviews","testi_goldman_sachs","testi_feedback_offer","testi_multiple_interviews"].map(name => (
              <div key={name} className="mx-3 w-[400px] flex-shrink-0">
                <img src={`/proof/${name}.jpg`} alt="User testimonial" className="w-full rounded-xl border border-white/10 shadow-lg hover:scale-[1.02] transition-transform duration-300" />
              </div>
            ))}
          </Marquee>
          <Marquee speed={30} direction="right" pauseOnHover gradient={false}>
            {["testi_ibm","testi_soulai_turing","testi_interviews_day2","testi_interviews_day3","testi_offer_letters","testi_deloitte_codex"].map(name => (
              <div key={name} className="mx-3 w-[400px] flex-shrink-0">
                <img src={`/proof/${name}.jpg`} alt="User testimonial" className="w-full rounded-xl border border-white/10 shadow-lg hover:scale-[1.02] transition-transform duration-300" />
              </div>
            ))}
          </Marquee>
        </motion.div>
      </Section>


      {/* ─── 10. TRUSTED BY / COMPANIES ────────────── */}
      <Section className="py-20" id="trusted">
        <Wrap className="text-center">
          <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-2">
            Users Have Landed Referrals At...
          </motion.h2>
          <motion.p variants={fadeUp} className="text-white/40 text-sm mb-10">(These are some of the companies our users have connected with)</motion.p>
        </Wrap>
        <motion.div variants={fadeUp}>
          <Marquee speed={50} pauseOnHover gradient={false}>
            {["Google", "Meta", "Amazon", "Microsoft", "Apple", "Netflix", "Stripe", "Uber", "Airbnb", "Salesforce", "Adobe", "LinkedIn"].map(c => (
              <div key={c} className="mx-6 px-8 py-4 bg-white/[0.04] border border-white/8 rounded-lg">
                <span className="text-white/50 text-lg font-bold">{c}</span>
              </div>
            ))}
          </Marquee>
        </motion.div>
      </Section>


      {/* ─── 12. LIVE RESULTS VIDEO ────────────────── */}
      <Section className="py-20" id="video-testimonials">
        <Wrap className="text-center">
          <motion.p variants={fadeUp} className="text-white/30 text-sm uppercase tracking-[0.3em] font-bold mb-3">PROOF</motion.p>
          <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-3">
            Don't Take Our Word For It
          </motion.h2>
          <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#FACC15] mb-4">
            Watch It In Action
          </motion.h2>
          <motion.p variants={fadeUp} className="text-white/40 text-sm mb-10 max-w-[600px] mx-auto">
            Real outreach. Real responses. Real referrals. All powered by ChillRefer.
          </motion.p>
          <motion.div variants={fadeUp} className="max-w-[900px] mx-auto">
            <div className="rounded-xl overflow-hidden border border-white/10 shadow-2xl" style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}>
              <iframe
                src="https://drive.google.com/file/d/1RzV3Y7uBbnVYbZU5RYw3swXFRjH9dVmN/preview"
                allow="autoplay; encrypted-media"
                allowFullScreen
                title="ChillRefer Live Results"
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
              />
            </div>
          </motion.div>
        </Wrap>
      </Section>


      {/* ─── 13. IF THEY CAN DO IT ─────────────────── */}
      <Section className="py-20 bg-[#09090b]" id="social-proof">
        <Wrap className="text-center">
          <motion.h2 variants={fadeUp} className="text-lg sm:text-xl text-white/60 mb-2">These are real job seekers getting real results.</motion.h2>
          <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl font-extrabold mb-4">and here's the best part...</motion.h2>
          <motion.div variants={fadeUp}>
            <ArrowDown size={32} className="text-[#FACC15] mx-auto mb-8" />
          </motion.div>

          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-8">
            If You Can <span className="underline decoration-[#FACC15] decoration-4 underline-offset-8">Type a Job Title,</span><br />
            You Can Land Referrals.
          </motion.h2>

          <motion.div variants={fadeUp}>
            <div className="grid grid-cols-4 md:grid-cols-8 gap-3 max-w-[900px] mx-auto">
              {[
                "https://static.prod-images.emergentagent.com/jobs/69b8ae2f-8b04-45a3-98d0-4d4bd6ae4938/images/c59c47c46003741b56c4c6c0d618bdeba3620e7ac6b79f61ca15e55f526a84c6.png",
                "https://static.prod-images.emergentagent.com/jobs/69b8ae2f-8b04-45a3-98d0-4d4bd6ae4938/images/18d6eb9ba93a0cfd78aeb71172db685ab20199c58ab76c7b335a1897e999f703.png",
                "https://images.unsplash.com/photo-1673830719944-7bf527816dae?w=120&h=120&fit=crop&crop=face",
                "https://static.prod-images.emergentagent.com/jobs/69b8ae2f-8b04-45a3-98d0-4d4bd6ae4938/images/a1296d5db2b1186f824d581c82627f805aed92f67e259682640ae6bafe8f51c5.png",
                "https://images.unsplash.com/photo-1609371497456-3a55a205d5eb?w=120&h=120&fit=crop&crop=face",
                "https://static.prod-images.emergentagent.com/jobs/69b8ae2f-8b04-45a3-98d0-4d4bd6ae4938/images/46d37311cb5cc2178c6905662af3ce851c21f64806eb57a829029674ac9c22cb.png",
                "https://images.unsplash.com/photo-1641108001784-cdf7d87b353f?w=120&h=120&fit=crop&crop=face",
                "https://images.unsplash.com/photo-1631606929991-200006582c09?w=120&h=120&fit=crop&crop=face",
              ].map((src, i) => (
                <img key={i} src={src} alt="ChillRefer user" className="w-full aspect-square rounded-full object-cover border-2 border-white/10 shadow-lg" />
              ))}
              {["RS","AK","NP","DM","VK","SP","RG","KS"].map((initials, i) => (
                <div key={`a-${i}`} className="w-full aspect-square rounded-full bg-gradient-to-br from-white/10 to-white/5 border-2 border-white/8 flex items-center justify-center">
                  <span className="text-white/30 text-[10px] font-bold">{initials}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.p variants={fadeUp} className="text-white/60 text-base md:text-lg leading-relaxed max-w-[850px] mx-auto mt-8">
            These are real users inside ChillRefer - software engineers, product managers, designers, data
            scientists who started exactly where you are.
            <strong className="text-white"> They didn't have insider connections. They weren't "well-networked."</strong> They
            just let the AI do the heavy lifting.<br /><br />
            Now they're landing interviews at dream companies, building genuine professional
            relationships, and getting referrals without the awkward cold outreach.
          </motion.p>

          <motion.h3 variants={fadeUp} className="text-2xl font-extrabold text-[#FACC15] mt-8 mb-8">If they can do it, so can YOU.</motion.h3>

          <motion.div variants={fadeUp}>
            <CTAButton onClick={goSignup}>
              Start Landing Referrals — $99/mo
              <span className="block text-xs font-semibold opacity-70 mt-1">Cancel anytime</span>
            </CTAButton>
            {/* CTA end */}
          </motion.div>
        </Wrap>
      </Section>


      {/* ─── 14. PROVEN TO WORK FOR (MARQUEE) ──────── */}
      <Section className="py-16" id="roles">
        <Wrap className="text-center mb-8">
          <motion.h2 variants={fadeUp} className="text-sm uppercase tracking-[0.2em] text-white/40 font-bold">PROVEN TO WORK FOR</motion.h2>
        </Wrap>
        <Marquee speed={40} gradient={false}>
          {["Software Engineers", "Product Managers", "Data Scientists", "UX Designers", "DevOps Engineers", "Marketing Managers", "Business Analysts", "Full-Stack Developers", "Machine Learning Engineers", "Startup Founders", "MBA Graduates", "Career Changers"].map(r => (
            <span key={r} className="mx-4 text-white/30 text-lg font-bold">{r}</span>
          ))}
        </Marquee>
      </Section>


      {/* ─── 15. BIG REVEAL ────────────────────────── */}
      <Section className="py-20 bg-[#09090b]" id="reveal">
        <Wrap className="text-center">
          <motion.p variants={fadeUp} className="text-[#FACC15] text-sm font-bold uppercase tracking-widest mb-4">big news:</motion.p>
          <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-8">
            For the first time ever,<br />
            we're giving you access to the <strong className="text-[#FACC15]">AI platform that automates referrals at any company</strong> without
            knowing anyone, writing awkward messages, or spending months networking...
          </motion.h2>
          <motion.p variants={fadeUp} className="text-white/50 text-base md:text-lg max-w-[850px] mx-auto">
            (even if you're applying to 10 companies and competing in the toughest job market
            where who-you-know decides who gets hired).
          </motion.p>
        </Wrap>
      </Section>


      {/* ─── 16. GO FROM X → Y ─────────────────────── */}
      <Section className="py-20" id="transform">
        <Wrap className="text-center">
          <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl font-extrabold text-white/40 mb-2">Go From</motion.h2>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-extrabold">
            100 Cold Apps/week <span className="text-[#FACC15]">&rarr;</span> 10 Warm Referrals/week
          </motion.h2>

          <motion.p variants={fadeUp} className="text-white/60 text-base md:text-lg leading-relaxed max-w-[850px] mx-auto mt-8">
            This isn't another job board, resume builder, or networking course. It's a <strong className="text-white">proven, repeatable,
            AI-powered platform</strong> built for how hiring actually works today where <strong className="text-white">warm introductions
            decide who gets the interview.</strong><br /><br />
            This is the same platform that has helped users connect with employees at every Fortune 500
            company, and convert those connections into real referrals.
          </motion.p>

          <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-6 mt-10 text-lg font-bold">
            {["No Cold Emails", "No Recruiter Games", "No Wasted Applications", "No Networking Anxiety"].map(t => (
              <span key={t} className="text-red-400"><X size={16} className="inline mr-1" />{t}</span>
            ))}
          </motion.div>

          <motion.p variants={fadeUp} className="text-white/70 text-base md:text-lg mt-10 max-w-[850px] mx-auto">
            Just you, AI, and a system that makes referrals inevitable.<br />
            <strong className="text-white">And here's what it has already helped generate...</strong>
          </motion.p>
        </Wrap>
      </Section>


      {/* ─── 17. STATS ─────────────────────────────── */}
      <Section className="py-16 bg-[#09090b]" id="stats">
        <Wrap>
          <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            {[
              ["10,000+", "Connection Requests Sent"],
              ["2,500+", "Referrals Generated"],
              ["500+", "Interviews Landed"],
            ].map(([num, label]) => (
              <div key={label} className="py-8">
                <p className="text-4xl sm:text-5xl font-black text-[#FACC15]">{num}</p>
                <p className="text-white/50 text-base font-semibold mt-2">{label}</p>
              </div>
            ))}
          </motion.div>
        </Wrap>
      </Section>


      {/* ─── 18. CASE STUDY ────────────────────────── */}
      <Section className="py-20" id="casestudy">
        <Wrap className="text-center">
          <motion.div variants={fadeUp} className="flex justify-center mb-6"><Award size={48} className="text-[#FACC15]" /></motion.div>
          <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-2">
            Case Study: How This AI System Generated
          </motion.h2>
          <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#FACC15] mb-8">
            50+ Interviews in 3 Months
          </motion.h2>
          <motion.p variants={fadeUp} className="text-white/40 text-sm font-bold uppercase tracking-widest mb-8">(Using the exact platform you're about to get access to)</motion.p>

          <motion.p variants={fadeUp} className="text-white/60 text-base md:text-lg leading-relaxed max-w-[850px] mx-auto">
            <strong className="text-white">READ THIS CAREFULLY</strong><br /><br />
            Over the last year, the job market has gone through one of its biggest shifts in
            how candidates are evaluated, selected, and hired.<br /><br />
            <span className="text-red-400">Resumes get lost in ATS.</span><br />
            <span className="text-red-400">Cold applications have &lt;2% response rate.</span><br />
            <span className="text-red-400">And "just applying online" simply doesn't work anymore.</span><br /><br />
            During this period, instead of fighting the system or constantly tweaking resumes,
            we focused on one thing only &mdash; <strong className="text-white">warm referrals at scale.</strong><br /><br />
            Using AI, we built a platform that automates the entire process: finding the right
            people to contact, crafting personalized outreach, and converting connections into real referrals.<br /><br />
            <strong className="text-white">This wasn't a side project.</strong> It was built specifically to solve the referral
            problem at scale — using the same AI engine you're about to access.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            <img src="/proof/result_goldman_sachs.jpg" alt="Goldman Sachs Interview Result" className="w-full rounded-xl border border-white/10 shadow-lg" />
            <img src="/proof/result_adyen.jpg" alt="Adyen Interview Result" className="w-full rounded-xl border border-white/10 shadow-lg" />
          </motion.div>
        </Wrap>
      </Section>


      {/* ─── 19. PAIN POINTS (4 sections) ──────────── */}
      {[
        {
          title: '"My applications keep going into a black hole..."',
          body: `Remember when you applied to your dream job, spent 2 hours customizing your resume, 
          wrote a perfect cover letter... and heard nothing back? Then it happened again. And again.
          100 applications later, you have 2 generic rejections and 98 silences.\n\nMeanwhile, you see people 
          on LinkedIn posting "Excited to share I'm joining Google!" and you can't help but wonder — what 
          are they doing differently?\n\nThe real fear isn't one rejection. It's not knowing if anything 
          you're doing actually works.`,
        },
        {
          title: '"I don\'t know anyone at these companies..."',
          body: `Everyone says "referrals are the best way to get hired." Great advice. But you don't know 
          anyone at Google. Or Amazon. Or the 50 other companies you're targeting.\n\nYou try LinkedIn — 
          scroll through hundreds of profiles, send a few awkward "Hi, I'm looking for a referral" messages, 
          and get ghosted. You feel like you're shouting into the void.\n\nYour competitor just got a warm intro 
          because they had a college friend at the company. You're still trying to find someone to message.
          \n\nYou're not unqualified. You're just using a system that depends on luck.`,
        },
        {
          title: '"Networking feels so awkward and fake..."',
          body: `You know you should be networking, but every time you send a connection request with a 
          message, you cringe. "Hi! I noticed we both went to..." feels rehearsed. "I'd love to connect 
          and learn about your experience at..." feels transactional.\n\nSo you don't send anything. 
          Or you send something generic and hope for the best. Then wonder why nobody responds.\n\n
          You're spending 15 hours a week on LinkedIn networking when you could be spending 15 minutes 
          with the right system.`,
        },
        {
          title: '"Job searching is taking over my entire life..."',
          body: `Job search was supposed to take a few weeks. It's been months. You're checking job boards 
          every morning, tweaking resumes every night, and the anxiety follows you everywhere.\n\n
          Every time you see someone land a job, your confidence drops a little more. You're not 
          failing because you're bad at what you do. You're exhausted because the system rewards 
          who-you-know, and you don't have a system that keeps up.\n\nWhen job search feels this 
          unstable, it's hard to stay motivated, confident, and focused on what actually matters.`,
        },
      ].map((pain, i) => (
        <Section key={i} className={`py-16 ${i % 2 === 0 ? "bg-[#09090b]" : ""}`}>
          <Wrap>
            <motion.h2 variants={fadeUp} className="text-xl sm:text-2xl lg:text-3xl font-extrabold mb-6">{pain.title}</motion.h2>
            <motion.p variants={fadeUp} className="text-white/60 text-base md:text-lg leading-relaxed max-w-[850px] whitespace-pre-line">{pain.body}</motion.p>
          </Wrap>
        </Section>
      ))}


      {/* ─── 20. WHY IS THIS HAPPENING ─────────────── */}
      <Section className="py-20" id="why">
        <Wrap className="text-center">
          <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-8">
            So... Why Is This Happening To You?
          </motion.h2>
          <motion.p variants={fadeUp} className="text-white/60 text-base md:text-lg max-w-[850px] mx-auto leading-relaxed">
            <strong className="text-white">Let me be honest with you.</strong> This isn't your fault.<br /><br />
            You didn't suddenly become unqualified.<br />
            Your skills didn't disappear.<br />
            You didn't miss some secret networking event.<br /><br />
            You're trying to compete in a <strong className="text-white">NEW game... using OLD rules.</strong>
          </motion.p>

          <motion.h3 variants={fadeUp} className="text-xl sm:text-2xl font-extrabold mt-12 mb-4 max-w-[850px] mx-auto">
            Here's what changed: <span className="text-[#FACC15]">Hiring now runs on warm introductions.</span>
          </motion.h3>
          <motion.div variants={fadeUp} className="text-white/60 text-base md:text-lg max-w-[850px] mx-auto leading-relaxed space-y-6">
            <p>
              <strong className="text-emerald-400">The candidates winning right now aren't more skilled than you.</strong> They
              don't have secret connections. They simply have systems that help them reach the right people
              faster. They send 50 personalized messages a week vs your 5. They get referrals because
              they have 10X more conversations.
            </p>
            <p>
              <strong className="text-red-400">But you can't network with 50 people per week.</strong> Not manually. Not with
              a full-time job or studies. Every personalized message means more research, more LinkedIn
              stalking, more crafting the perfect note. So you're forced to network less... and hope more.
            </p>
            <p className="text-white font-bold text-lg">
              Unless... you stop networking manually and let AI do it systematically.
              That's exactly what ChillRefer does...
            </p>
          </motion.div>
        </Wrap>
      </Section>


      {/* ─── 21. INTRODUCING CHILLREFER ────────────── */}
      <Section className="py-20 bg-[#09090b]" id="introducing">
        <Wrap className="text-center">
          <motion.p variants={fadeUp} className="text-white/30 text-sm uppercase tracking-[0.3em] font-bold mb-4">INTRODUCING</motion.p>
          <motion.h2 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#FACC15] mb-6">ChillRefer</motion.h2>
          <motion.p variants={fadeUp} className="text-white/60 text-base md:text-lg max-w-[850px] mx-auto leading-relaxed mb-10">
            The most simple & powerful AI-powered referral platform that finds, connects with,
            and gets you referred by employees at any company — automatically.
          </motion.p>
          <motion.div variants={fadeUp}>
            <p className="text-white/40 text-sm font-bold uppercase tracking-[0.2em] text-center mb-4">Platform Demo</p>
            <div className="rounded-xl overflow-hidden border border-white/10 shadow-2xl">
              <video autoPlay muted loop playsInline poster="/demo_poster.jpg" className="w-full">
                <source src="/chillrefer_demo.mp4" type="video/mp4" />
              </video>
            </div>
            <div className="text-center mt-4">
              <a
                href="/product-demo"
                className="inline-flex items-center gap-1.5 text-sm text-[#FACC15]/80 hover:text-[#FACC15] font-semibold"
                data-testid="landing-watch-full-demo"
              >
                Watch the full demo with chapters & sound <ArrowRight size={13} />
              </a>
            </div>
          </motion.div>

          <motion.div variants={fadeUp} className="max-w-[850px] mx-auto mt-12 space-y-6">
            <p className="text-white font-bold text-lg">Let me be very clear from the start</p>
            <p className="text-white/60 text-base md:text-lg leading-relaxed">
              This is NOT some random Chrome extension.<br />
              This is NOT recycled LinkedIn tips from 2023.<br />
              And this is DEFINITELY NOT "spray and pray" connection requests.<br /><br />
              <strong className="text-white">ChillRefer is a complete AI platform</strong> we built by actually
              helping people land referrals at companies they never thought possible. We tested what works,
              what fails, what scales, and refined the product until the process became simple, repeatable, and predictable.
            </p>
          </motion.div>
        </Wrap>
      </Section>


      {/* ─── 22. FEATURE MODULES ───────────────────── */}
      <Section className="py-20" id="features">
        <Wrap>
          <motion.h2 variants={fadeUp} className="text-center text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-4">
            Here's everything you get inside
          </motion.h2>
          <motion.h2 variants={fadeUp} className="text-center text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#FACC15] mb-16">ChillRefer</motion.h2>

          {[
            {
              num: "01", title: "AI Job Discovery",
              desc: "Search thousands of LinkedIn jobs directly inside ChillRefer with smart filters — role, location, company, experience level. One click to save any job to your tracking Kanban. No more tab-switching between 10 job boards.",
              features: ["Smart LinkedIn Job Search", "Advanced Filtering (location, company, role)", "1-Click Save to Jobs Kanban", "AI-Recommended Jobs Based on Your Profile"],
              bonus: "AI auto-categorizes jobs by match strength so you focus on the highest-probability opportunities first.",
            },
            {
              num: "02", title: "Referral Hunter AI",
              desc: "For any saved job, ChillRefer's AI instantly finds the best people to contact at that company — current employees in your target team, hiring managers, and warm connections. No more guessing who to reach out to.",
              features: ["AI-Powered People Discovery", "Filter by Department, Seniority, Location", "Mutual Connection Detection", "One-Click Add to Network Pipeline"],
              bonus: "The AI prioritizes people most likely to respond based on their activity patterns and connection overlap.",
            },
            {
              num: "03", title: "Automated Personalized Outreach",
              desc: "ChillRefer crafts unique, personalized connection requests for each person — referencing their background, shared interests, and the specific role. Then sends them automatically. You approve, AI executes.",
              features: ["AI Message Generation (unique per person)", "Batch Send 50+ Connections in Minutes", "Smart Rate-Limit Protection", "Auto-Fallback for Non-Premium Accounts"],
              bonus: "Built-in LinkedIn safety net ensures you never trigger account restrictions. The system auto-throttles to safe limits.",
            },
            {
              num: "04", title: "Smart Follow-Up Engine",
              desc: "Most referrals happen after the follow-up, not the first message. ChillRefer automatically schedules and sends thoughtful follow-ups to connections who haven't responded — at exactly the right time.",
              features: ["Automated Follow-Up Sequences", "Customizable Timing & Templates", "Response Detection (stops when they reply)", "Follow-Up Performance Analytics"],
              bonus: "Follow-up messages are dynamically personalized — never generic \"just following up\" messages.",
            },
            {
              num: "05", title: "AI Command Center (Copilot)",
              desc: "Tell ChillRefer what to do in plain English: \"Find me referrers at Stripe for the Senior PM role\" and watch the AI execute — searching jobs, finding people, sending connections, all from a single chat interface.",
              features: ["Natural Language Commands", "Multi-Step Task Execution", "Context-Aware Conversations", "Rich Card Results (Jobs, Profiles, Stats)"],
              bonus: "The Copilot remembers your conversation context — ask follow-up questions and it picks up exactly where you left off.",
            },
          ].map((mod, i) => (
            <motion.div key={i} variants={fadeUp} className={`flex flex-col md:flex-row gap-8 py-12 ${i > 0 ? "border-t border-white/8" : ""}`}>
              <div className="md:w-1/3 flex-shrink-0">
                <span className="text-[#FACC15] text-sm font-bold uppercase tracking-widest">FEATURE {mod.num}</span>
                <h3 className="text-2xl font-extrabold mt-2">{mod.title}</h3>
                <FeatureMockup type={mod.num} />
              </div>
              <div className="md:w-2/3">
                <p className="text-white/60 text-base leading-relaxed mb-6">{mod.desc}</p>
                {mod.features.map((l, j) => (
                  <div key={j} className="flex items-center gap-2 py-1">
                    <CheckCircle size={14} className="text-emerald-400 flex-shrink-0" />
                    <span className="text-white/70 text-sm">{l}</span>
                  </div>
                ))}
                <div className="mt-4 bg-[#FACC15]/5 border border-[#FACC15]/15 rounded-lg p-3">
                  <span className="text-[#FACC15] text-xs font-bold uppercase tracking-widest">BONUS:</span>
                  <p className="text-white/60 text-sm mt-1">{mod.bonus}</p>
                </div>
              </div>
            </motion.div>
          ))}

          <motion.div variants={fadeUp} className="text-center mt-12">
            <CTAButton onClick={goSignup}>
              Start Landing Referrals — $99/mo
              <span className="block text-xs font-semibold opacity-70 mt-1">Cancel anytime</span>
            </CTAButton>
            {/* CTA end */}
          </motion.div>
        </Wrap>
      </Section>


      {/* ─── 23. BEFORE / AFTER ────────────────────── */}
      <Section className="py-20 bg-[#09090b]" id="before-after">
        <Wrap className="text-center">
          <motion.h2 variants={fadeUp} className="text-sm uppercase tracking-[0.2em] text-white/40 font-bold mb-2">Job Search On "Easy Mode" with AI</motion.h2>
          <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl font-extrabold mb-12">
            This Is What Happens When You Stop Applying Cold...<br />And Start Using AI Referrals Systematically
          </motion.h2>

          <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-8 text-left">
              <p className="text-2xl font-extrabold mb-6">BEFORE ChillRefer</p>
              {[
                "Takes weeks to get a single interview",
                "Applications going into ATS black hole",
                "Only applying to 10-20 jobs/month",
                "Watching response rate slowly drop to 0%",
                "Dependent on luck and existing network",
                "No idea who to contact at companies",
                "Losing confidence with every rejection",
              ].map((t, i) => (
                <div key={i} className="flex items-start gap-2 py-1.5"><X size={16} className="text-red-400 mt-1 flex-shrink-0" /><span className="text-white/60 text-sm">{t}</span></div>
              ))}
            </div>
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-8 text-left">
              <p className="text-2xl font-extrabold mb-6">AFTER ChillRefer</p>
              {[
                "Landing 3-5 interviews per week",
                "Bypassing ATS with warm referrals",
                "Connecting with 50+ people per week",
                "Response rate 5X higher than cold apps",
                "AI finds the right people automatically",
                "Personalized outreach sent in minutes",
                "Confident knowing referrals are flowing",
              ].map((t, i) => (
                <div key={i} className="flex items-start gap-2 py-1.5"><CheckCircle size={16} className="text-emerald-400 mt-1 flex-shrink-0" /><span className="text-white/80 text-sm">{t}</span></div>
              ))}
            </div>
          </motion.div>
        </Wrap>
      </Section>


      {/* ─── 24. FOUNDER / ABOUT ───────────────────── */}
      <Section className="py-20" id="about">
        <Wrap>
          <motion.div variants={fadeUp} className="flex flex-col md:flex-row gap-10 items-center">
            <div className="md:w-2/5 flex-shrink-0">
              <div className="relative mx-auto max-w-[300px]">
                <div className="absolute -inset-2 bg-gradient-to-b from-[#FACC15]/20 to-transparent rounded-2xl blur-xl opacity-40" />
                <img src="/founder_headshot.jpg" alt="Shubham Maheshwari — Founder, ChillRefer" className="relative rounded-2xl w-full aspect-[3/4] object-cover border-2 border-white/10 shadow-2xl" style={{objectPosition: "50% 25%"}} />
              </div>
            </div>
            <div className="md:w-3/5">
              <p className="text-white/40 text-sm uppercase tracking-widest font-bold mb-2">Dear Fellow Job Seeker,</p>
              <h2 className="text-3xl font-extrabold mb-5">A Letter from the Founder</h2>
              <div className="h-0.5 w-16 bg-[#FACC15] mb-5" />
              <p className="text-white/60 text-base leading-relaxed">
                I know exactly what you're going through. The endless applications, the silence that follows,
                the frustration of not hearing back. I've been there myself, and I've watched countless
                talented professionals struggle with the same broken system.<br /><br />
                After years of working in corporate roles across Dubai, I realized something important:
                <strong className="text-white"> the job market rewards connections, but most people simply don't
                have the network to get referrals at scale while maintaining authenticity.</strong> That's why I built ChillRefer.<br /><br />
                We're not another job board or resume-blasting tool. We're an <strong className="text-white">AI-powered referral platform</strong> that
                understands how hiring actually works — through warm introductions and genuine connections. ChillRefer finds the right
                people at your target companies, crafts personalized outreach, and manages the entire referral pipeline
                so you can focus on what matters: preparing for interviews and landing your dream role.<br /><br />
                If you're tired of shouting into the void with cold applications, let's change that. I personally
                believe every talented professional deserves a fair shot — and <strong className="text-white">ChillRefer is built to make that happen.</strong>
              </p>
              <div className="mt-6 pt-5 border-t border-white/8">
                <p className="text-white font-extrabold text-lg">Shubham Maheshwari</p>
                <p className="text-[#FACC15] text-sm font-bold">Founder, ChillRefer</p>
                <p className="text-white/40 text-xs mt-1">Ex Lead Trader at Inspire International Trading DMCC, Dubai</p>
                <p className="text-white/40 text-xs">Ex Business Excellence Engineer at Shobha Construction, Dubai</p>
              </div>
            </div>
          </motion.div>
        </Wrap>
      </Section>


      {/* ─── 25. IMAGINE TIMELINE ──────────────────── */}
      <Section className="py-20 bg-[#09090b]" id="imagine">
        <Wrap className="text-center">
          <motion.div variants={fadeUp}>
            <div className="relative w-[180px] h-[180px] mx-auto mb-8">
              <div className="absolute -inset-1 rounded-full bg-gradient-to-b from-[#FACC15]/30 to-transparent blur-md" />
              <img src="https://customer-assets.emergentagent.com/job_69b8ae2f-8b04-45a3-98d0-4d4bd6ae4938/artifacts/0w338w8o_me.jpeg" alt="Shubham Maheshwari" className="relative w-full h-full rounded-full object-cover border-2 border-[#FACC15]/20" />
            </div>
          </motion.div>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold mb-2">Imagine</motion.h2>
          <motion.p variants={fadeUp} className="text-white/50 text-base mb-12">Here's what starts happening after you start using ChillRefer:</motion.p>

          <motion.div variants={fadeUp} className="text-left max-w-[900px] mx-auto space-y-6">
            {[
              ["Day 1:", "You set up your profile, connect LinkedIn, and the AI discovers 50 potential referrers at your target companies."],
              ["Day 3-5:", "You've sent 100+ personalized connection requests. First acceptances start rolling in."],
              ["Week 1:", "You have 20+ new connections at target companies. First conversations about referrals begin."],
              ["Week 2:", "Your first referral comes through. You land an interview at a company you'd been cold applying to for months."],
              ["Month 1:", "You've built a genuine network of 100+ professionals. Multiple referrals in pipeline. Interviews every week."],
              ["Month 3:", "You've landed your dream role through a referral. Your professional network is 5X larger. The job search stress is over."],
            ].map(([time, desc], i) => (
              <div key={i} className="flex gap-4 items-start">
                <div className="w-2 h-2 rounded-full bg-[#FACC15] mt-2.5 flex-shrink-0" />
                <div>
                  <span className="text-[#FACC15] font-bold text-base">{time}</span>
                  <p className="text-white/60 text-sm md:text-base mt-1">{desc}</p>
                </div>
              </div>
            ))}
          </motion.div>

          <motion.h3 variants={fadeUp} className="text-xl font-extrabold mt-12">
            That's the shift ChillRefer is designed to deliver.
          </motion.h3>
          <motion.p variants={fadeUp} className="text-white/50 text-base mt-2">
            Not just finding jobs, but an AI platform that lands you referrals while doing less work.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-10">
            <CTAButton onClick={goSignup}>
              Start Landing Referrals — $99/mo
              <span className="block text-xs font-semibold opacity-70 mt-1">Cancel anytime</span>
            </CTAButton>
            {/* CTA end */}
          </motion.div>
        </Wrap>
      </Section>


      {/* ─── 26. CHOICE: OLD WAY vs NEW WAY ────────── */}
      <Section className="py-20" id="choice">
        <Wrap className="text-center">
          <motion.div variants={fadeUp}>
            <div className="h-40 max-w-[300px] mx-auto rounded-xl overflow-hidden mb-8 border border-white/10">
              <img src="https://images.unsplash.com/photo-1762920738995-f393efe82205?w=400&h=200&fit=crop" alt="Crossroads" className="w-full h-full object-cover opacity-60" />
            </div>
          </motion.div>
          <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-8">
            You now have a choice to make
          </motion.h2>
          <motion.p variants={fadeUp} className="text-white/60 text-base md:text-lg max-w-[850px] mx-auto">
            Do you want to keep cold applying to hundreds of jobs and hearing nothing back...
            or do you want an AI platform that finds referrers, sends personalized outreach, and
            lands you interviews on autopilot?
          </motion.p>
        </Wrap>
      </Section>


      {/* ─── 27. TEXT REVIEWS MARQUEE ───────────────── */}
      <Section className="py-16 bg-[#09090b]" id="reviews">
        <Wrap className="text-center mb-8">
          <motion.h2 variants={fadeUp} className="text-xl font-extrabold">What users are saying...</motion.h2>
        </Wrap>
        <Marquee speed={30} pauseOnHover gradient={false}>
          {[
            "First referral in 3 days! This is incredible.",
            "I was cold applying for 6 months. ChillRefer changed everything in 2 weeks.",
            "The AI messages are so personalized, people actually respond.",
            "Landed my dream job at Google through a referral from ChillRefer.",
            "I send 50 connections a day now. Used to struggle with 5.",
            "The follow-up automation is genius. Got 3 referrals from follow-ups alone.",
            "Best investment I've made in my career. Period.",
            "Finally feel like I'm in control of my job search.",
          ].map((r, i) => (
            <div key={i} className="mx-3 px-6 py-4 bg-white/[0.03] border border-white/8 rounded-lg max-w-[320px]">
              <p className="text-white/60 text-sm italic">"{r}"</p>
            </div>
          ))}
        </Marquee>
      </Section>


      {/* ─── 28. PRICING TIERS ─────────────────────── */}
      <Section className="py-20" id="pricing">
        <Wrap className="text-center">
          <motion.h2 variants={fadeUp} className="text-sm uppercase tracking-[0.2em] text-white/40 font-bold mb-2">Choose your plan</motion.h2>
          <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl font-extrabold mb-12">
            Start landing referrals today.
          </motion.h2>

          <motion.div variants={fadeUp} className="max-w-[500px] mx-auto">
            <div className="bg-[#09090b] border-2 border-[#FACC15]/40 rounded-xl p-8 text-left relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FACC15] text-black text-xs font-black uppercase tracking-widest px-4 py-1 rounded-full">MOST POPULAR</div>
              <p className="text-[#FACC15] text-sm font-bold uppercase tracking-widest mb-1">Pro</p>
              <p className="text-white/30 text-sm mb-2">Full AI Referral System</p>
              <p className="text-white/30 text-xl line-through">$199/month</p>
              <p className="text-4xl font-black text-white">$99<span className="text-lg font-semibold text-white/50">/month</span></p>
              <p className="text-emerald-400 text-sm font-bold mt-1 mb-6">Save $100/month</p>
              <div className="space-y-2 mb-8">
                {[
                  "Unlimited AI Job Discovery",
                  "Unlimited Referral Hunting",
                  "50+ Connection Requests/day",
                  "Automated Follow-Up Sequences",
                  "ChillBot AI Copilot (Full Power)",
                  "36 Message Templates Library",
                  "Jobs & Network Kanban Boards",
                  "Real-Time Reply Detection",
                  "Priority Support",
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-2"><CheckCircle size={14} className="text-emerald-400" /><span className="text-white/80 text-sm">{f}</span></div>
                ))}
              </div>
              <button onClick={goSignup} className="w-full py-3.5 bg-[#FACC15] text-black font-extrabold text-base rounded-lg hover:bg-[#eab308] transition-all hover:scale-[1.02]">
                Start Landing Referrals — $99/mo
              </button>
              <p className="text-center text-emerald-400 text-xs font-bold mt-3"><Shield size={12} className="inline mr-1" />Cancel Anytime &middot; No Contracts</p>
            </div>

            <div className="mt-6">
              <OutcomeGuarantee variant="banner" />
            </div>
          </motion.div>
        </Wrap>
      </Section>


      {/* ─── 29. FAQ ───────────────────────────────── */}
      <Section className="py-20 bg-[#09090b]" id="faq">
        <Wrap>
          <motion.h2 variants={fadeUp} className="text-center text-2xl sm:text-3xl font-extrabold mb-12">Frequently Asked Questions</motion.h2>
          <motion.div variants={fadeUp} className="max-w-[850px] mx-auto">
            {[
              { q: "What is ChillRefer?", a: "ChillRefer is an AI-powered SaaS platform that automates the entire referral job search process. It discovers jobs, finds the right people to contact at those companies, crafts personalized outreach messages, sends connection requests, and manages follow-ups — all automatically." },
              { q: "Do I need LinkedIn Premium?", a: "No! ChillRefer works with any LinkedIn account, free or premium. Our AI uses smart workarounds and the Unipile API to deliver full functionality without requiring you to upgrade your LinkedIn plan." },
              { q: "How is this different from just applying on LinkedIn?", a: "Cold applications on LinkedIn have a <2% response rate. ChillRefer gets you warm referrals which have a 40-60% interview conversion rate. Instead of throwing your resume into the ATS black hole, you're getting introduced by someone who already works at the company." },
              { q: "Will this work for my industry/role?", a: "Yes. ChillRefer works for any role where companies hire through LinkedIn — software engineering, product management, data science, marketing, design, finance, consulting, and more. If the company has employees on LinkedIn, ChillRefer can find your referrers." },
              { q: "How soon can I start getting referrals?", a: "Most users send their first batch of connection requests within 30 minutes of signing up. First connections start accepting within 24-48 hours. First referral conversations typically happen within the first week." },
              { q: "Is my LinkedIn account safe?", a: "Absolutely. ChillRefer has a built-in safety engine that monitors your daily connection limits, automatically throttles requests to stay within LinkedIn's guidelines, and uses smart fallback mechanisms to protect your account." },
              { q: "Can I cancel anytime?", a: "Yes. No contracts or commitments. Cancel your Pro subscription at any time from your Billing page. Your access continues until the end of the current billing period." },
              { q: "What if it doesn't work for me?", a: "Start with a free account and let ChillBot AI show you exactly how ChillRefer works. When you're ready for LinkedIn automation, upgrade to Pro. You can cancel anytime from your Billing page — no questions asked." },
            ].map((faq, i) => <FAQItem key={i} q={faq.q} a={faq.a} />)}
          </motion.div>
        </Wrap>
      </Section>


      {/* ─── 30. PS / PERSONAL MESSAGE ─────────────── */}
      <Section className="py-20" id="ps">
        <Wrap>
          <motion.div variants={fadeUp} className="flex flex-col md:flex-row gap-8 items-center max-w-[900px] mx-auto">
            <div className="flex-shrink-0">
              <img src="/founder_headshot.jpg" alt="Shubham Maheshwari" className="rounded-2xl w-[160px] h-[160px] object-cover border-2 border-white/10 shadow-lg" style={{objectPosition: "50% 25%"}} />
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-extrabold mb-3">A short personal message for you</h3>
              <div className="h-0.5 w-12 bg-[#FACC15] mb-4" />
              <p className="text-white/60 text-base leading-relaxed">
                <strong className="text-white">P.S.</strong> Since you've made it this far, it's clear you don't want to keep
                cold applying and hoping for the best. You want an AI platform that finds the right
                people, sends the right messages, and lands you real referrals.<br /><br />
                <strong className="text-white">P.S.S.</strong> The job market rewards connected people. The candidates who start
                using AI for networking NOW will be the ones landing offers while others are still
                refreshing their inbox hoping for a response.<br /><br />
                <strong className="text-white">All that's left for you to do is:</strong> Click the button below and create your free account today.
              </p>
            </div>
          </motion.div>
        </Wrap>
      </Section>


      {/* ─── 31. FINAL CTA ─────────────────────────── */}
      <Section className="py-20 bg-[#09090b]" id="final-cta">
        <Wrap className="text-center">
          <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-4">
            Still thinking about it?
          </motion.h2>
          <motion.p variants={fadeUp} className="text-white/50 text-base md:text-lg max-w-[900px] mx-auto mb-10">
            Every day you spend cold applying is another day someone less qualified lands the job through a referral.
            Don't wait. Start today.
          </motion.p>

          <motion.div variants={fadeUp}>
            <CTAButton onClick={goSignup}>
              Start Landing Referrals — $99/mo
              <span className="block text-xs font-semibold opacity-70 mt-1">Cancel anytime</span>
            </CTAButton>
          </motion.div>

          <motion.p variants={fadeUp} className="text-white/30 text-sm mt-6">
            500+ Happy Users &middot; Cancel Anytime &middot; No Contracts
          </motion.p>
        </Wrap>
      </Section>


      {/* ─── 32. FOOTER ────────────────────────────── */}
      <footer className="border-t border-white/5">
        {/* Trust Badges Bar */}
        <div className="py-8 border-b border-white/5">
          <Wrap>
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
              {[
                { icon: <Shield size={18} />, label: "SSL Encrypted" },
                { icon: <Lock size={18} />, label: "Data Protected" },
                { icon: <CheckCircle size={18} />, label: "Secure Payments" },
                { icon: <UserCheck size={18} />, label: "GDPR Compliant" },
                { icon: <Eye size={18} />, label: "No Data Selling" },
              ].map((badge, i) => (
                <div key={i} className="flex items-center gap-2 text-white/30">
                  <span className="text-white/20">{badge.icon}</span>
                  <span className="text-xs font-semibold tracking-wide">{badge.label}</span>
                </div>
              ))}
            </div>
          </Wrap>
        </div>

        {/* Main Footer Content */}
        <div className="py-12">
          <Wrap>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {/* Brand */}
              <div className="col-span-2 md:col-span-1">
                <Logo size="sm" />
                <p className="text-white/30 text-xs mt-3 leading-relaxed max-w-[220px]">
                  AI-powered referral automation. Land interviews at top companies through warm introductions.
                </p>
              </div>

              {/* Product */}
              <div>
                <h4 className="text-white/50 text-xs font-bold uppercase tracking-widest mb-4">Product</h4>
                <ul className="space-y-2.5">
                  {[
                    { label: "Features", href: "#features" },
                    { label: "Pricing", href: "#pricing" },
                    { label: "FAQ", href: "#faq" },
                    { label: "How It Works", href: "#introducing" },
                    { label: "Watch Demo", href: "/product-demo" },
                    { label: "Compare alternatives", href: "/alternatives" },
                  ].map((link, i) => (
                    <li key={i}>
                      <a href={link.href} className="text-white/30 hover:text-white/60 text-sm transition-colors">{link.label}</a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Resources */}
              <div>
                <h4 className="text-white/50 text-xs font-bold uppercase tracking-widest mb-4">Resources</h4>
                <ul className="space-y-2.5">
                  {[
                    { label: "Blog", href: "/blog" },
                    { label: "Free Templates", href: "/templates" },
                    { label: "Free Tools", href: "/tools" },
                    { label: "Resignation Letters", href: "/resignation-letter" },
                    { label: "Resume Headlines", href: "/resume-headline" },
                    { label: "Video Course", href: "/learn" },
                    { label: "Job Roles Index", href: "/jobs" },
                  ].map((link, i) => (
                    <li key={i}>
                      <a href={link.href} className="text-white/30 hover:text-white/60 text-sm transition-colors">{link.label}</a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Legal + Contact */}
              <div>
                <h4 className="text-white/50 text-xs font-bold uppercase tracking-widest mb-4">Legal</h4>
                <ul className="space-y-2.5 mb-6">
                  <li><a href="/terms" className="text-white/30 hover:text-white/60 text-sm transition-colors">Terms & Conditions</a></li>
                  <li><a href="/privacy" className="text-white/30 hover:text-white/60 text-sm transition-colors">Privacy Policy</a></li>
                  <li><a href="/refund" className="text-white/30 hover:text-white/60 text-sm transition-colors">Refund Policy</a></li>
                </ul>
                <h4 className="text-white/50 text-xs font-bold uppercase tracking-widest mb-3">Contact</h4>
                <ul className="space-y-2">
                  <li><a href="mailto:support@chillrefer.com" className="text-white/30 hover:text-white/60 text-sm transition-colors">support@chillrefer.com</a></li>
                  <li><span className="text-white/30 text-sm">Bengaluru, India</span></li>
                </ul>
              </div>
            </div>

            {/* Get a referral at — deep cross-linking to /refer/{company} pages.
                This is the SEO bedrock: every page on chillrefer.com links to
                these company pages so Google sees them as important and
                crawls/indexes them. Previously they were orphan URLs only
                discoverable via sitemap. */}
            <div className="mt-12 pt-10 border-t border-white/5">
              <h4 className="text-white/50 text-xs font-bold uppercase tracking-widest mb-5">Get a referral at top companies</h4>
              <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-white/35">
                {[
                  "google","meta","amazon","apple","netflix","microsoft","stripe","airbnb",
                  "uber","tesla","openai","anthropic","nvidia","salesforce","oracle","ibm",
                  "intel","amd","atlassian","shopify","coinbase","robinhood","doordash","instacart",
                  "cloudflare","palantir","figma","notion","linear","vercel","datadog","okta",
                  "twilio","plaid","brex","ramp","razorpay","swiggy","zomato","flipkart",
                  "phonepe","paytm","myntra","zerodha","freshworks","zoho","hubspot","databricks",
                  "snowflake","canva","airtable","asana","klaviyo","scale-ai","spacex","rubrik",
                  "lyft","block","checkr","careem","talabat","noon",
                ].map((slug, i) => (
                  <a
                    key={i}
                    href={`/refer/${slug}`}
                    className="hover:text-[#FACC15] hover:underline transition-colors capitalize"
                  >
                    {slug}
                  </a>
                ))}
              </div>
            </div>

            {/* Top job roles — internal links to /jobs/{role}-at-{company} curated combos. */}
            <div className="mt-10">
              <h4 className="text-white/50 text-xs font-bold uppercase tracking-widest mb-5">Land roles at top companies</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2 text-xs text-white/35">
                {[
                  ["software-engineer-at-google","Software Engineer @ Google"],
                  ["software-engineer-at-meta","Software Engineer @ Meta"],
                  ["software-engineer-at-stripe","Software Engineer @ Stripe"],
                  ["software-engineer-at-amazon","Software Engineer @ Amazon"],
                  ["software-engineer-at-microsoft","SWE @ Microsoft"],
                  ["software-engineer-at-openai","SWE @ OpenAI"],
                  ["software-engineer-at-apple","SWE @ Apple"],
                  ["software-engineer-at-netflix","SWE @ Netflix"],
                  ["product-manager-at-google","PM @ Google"],
                  ["product-manager-at-meta","PM @ Meta"],
                  ["product-manager-at-amazon","PM @ Amazon"],
                  ["product-manager-at-stripe","PM @ Stripe"],
                  ["data-scientist-at-google","Data Scientist @ Google"],
                  ["data-scientist-at-meta","Data Scientist @ Meta"],
                  ["data-scientist-at-amazon","Data Scientist @ Amazon"],
                  ["data-scientist-at-microsoft","Data Scientist @ Microsoft"],
                ].map(([slug, label], i) => (
                  <a
                    key={i}
                    href={`/jobs/${slug}`}
                    className="hover:text-[#FACC15] hover:underline transition-colors"
                  >
                    {label}
                  </a>
                ))}
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="mt-10 pt-6 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-white/20 text-xs">
                &copy; {new Date().getFullYear()} Knowfinity Academy LLP. All Rights Reserved.
              </p>
              <p className="text-white/15 text-[10px] max-w-[500px] text-center md:text-right">
                This site is not a part of LinkedIn or Microsoft Corporation. LinkedIn is a trademark of LinkedIn Corporation.
              </p>
            </div>
          </Wrap>
        </div>
      </footer>

      {/* ─── STICKY BOTTOM BAR — appears after hero scrolls out ─── */}
      <AnimatePresence>
        {showStickyBar && !user && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-0 left-0 right-0 z-40 bg-[#09090b]/95 backdrop-blur-md border-t border-white/10"
            data-testid="sticky-bottom-bar"
          >
            <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
              <div className="hidden sm:block text-xs text-white/65 leading-tight">
                <strong className="text-white">{liveCount}</strong> people viewing this · See who can refer you in 30s
              </div>
              <div className="flex items-center gap-2 flex-1 sm:flex-initial">
                <button
                  onClick={() => {
                    document.getElementById("hero")?.scrollIntoView({ behavior: "smooth", block: "start" });
                    setTimeout(() => document.querySelector('[data-testid="discovery-url-input"]')?.focus(), 700);
                  }}
                  data-testid="sticky-paste-url"
                  className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-white/[0.06] border border-white/15 text-white/85 hover:text-white hover:border-white/30 text-xs sm:text-sm font-bold transition-all"
                >
                  <Sparkles size={13} className="text-[#FACC15]" /> Paste a job link
                </button>
                <button
                  onClick={goSignup}
                  data-testid="sticky-start-pro"
                  className="inline-flex items-center gap-1.5 px-4 sm:px-5 py-2.5 rounded-md bg-[#FACC15] hover:bg-[#fde047] text-black font-black text-xs sm:text-sm transition-all hover:scale-[1.03] shadow-[0_0_20px_rgba(250,204,21,0.25)]"
                >
                  Start Pro · $99/mo <ArrowRight size={13} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── EXIT INTENT MODAL ─── */}
      <AnimatePresence>
        {showExitIntent && !user && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowExitIntent(false)}
            data-testid="exit-intent-overlay"
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative bg-[#0f0f12] border border-[#FACC15]/30 rounded-2xl max-w-[480px] w-full p-7 sm:p-8 shadow-[0_0_60px_rgba(250,204,21,0.15)]"
            >
              <button
                onClick={() => setShowExitIntent(false)}
                className="absolute top-3 right-3 text-white/30 hover:text-white/70 transition-colors"
                aria-label="Close"
              >
                ✕
              </button>
              <p className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FACC15]/10 border border-[#FACC15]/30 text-[10.5px] tracking-wider uppercase text-[#FACC15] font-semibold mb-4">
                <Sparkles size={11} /> Before you go
              </p>
              <h3 className="text-2xl sm:text-3xl font-black leading-tight mb-3">
                Try 1 free search.<br/>
                <span className="text-[#FACC15]">See who can refer you</span> in 30s.
              </h3>
              <p className="text-white/55 text-sm leading-relaxed mb-6">
                Paste any LinkedIn job URL — we'll show you 3 real hiring managers + personalised connection notes. No signup needed.
              </p>
              <button
                onClick={() => {
                  setShowExitIntent(false);
                  document.getElementById("hero")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  setTimeout(() => document.querySelector('[data-testid="discovery-url-input"]')?.focus(), 700);
                }}
                data-testid="exit-intent-cta"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-md bg-[#FACC15] hover:bg-[#fde047] text-black font-black text-sm transition-all hover:scale-[1.02]"
              >
                Try the free search → <ArrowRight size={14} />
              </button>
              <p className="text-[11px] text-white/35 mt-3 text-center">
                Already a Pro user? <button onClick={() => navigate("/login")} className="text-white/65 hover:text-[#FACC15] underline">Login here</button>
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
